import { type FC, useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { DialogComponent, Radio, RadioGroup, Switch, VStack } from "widgets";

import { updateUserParams } from "@/redux/modules/auth.ts";
import type { IAppState } from "@/redux/reducer.ts";
import { type ReduxDispatch } from "@/redux/redux-utils.ts";

interface IFlagTrackCustomizationDialogProps {
  onClose: () => void;
}

enum COLORING_SCHEME_OPTION {
  BY_FLAG_COLOR = "BY_FLAG_COLOR",
  BY_PROJECT_COLOR = "BY_PROJECT_COLOR",
}
const colorSchemeOptions = [
  {
    key: COLORING_SCHEME_OPTION.BY_FLAG_COLOR,
    label: "Flag Color",
    description: "The flags will be filled using the color defined for every flag",
  },
  {
    key: COLORING_SCHEME_OPTION.BY_PROJECT_COLOR,
    label: "Project Color",
    description: "The flags will be filled using the project color (default)",
  },
];

const FlagTrackCustomizationDialog: FC<IFlagTrackCustomizationDialogProps> = (props) => {
  const dispatch = useDispatch<ReduxDispatch>();

  const useSpecificFlagsColoring = useSelector<IAppState, boolean>(
    (state) => state.auth.session.params.useSpecificFlagsColoring,
  );
  const condensedFlagsDisplay = useSelector<IAppState, boolean>(
    (state) => state.auth.session.params.condensedFlagsDisplay,
  );
  const defaultColoringSchemeOptionKey = useSpecificFlagsColoring
    ? COLORING_SCHEME_OPTION.BY_FLAG_COLOR
    : COLORING_SCHEME_OPTION.BY_PROJECT_COLOR;
  const [selectedKey, setSelectedKey] = useState<COLORING_SCHEME_OPTION>(defaultColoringSchemeOptionKey);
  const [condensedRegionFlags, setCondensedRegionFlags] = useState<boolean>(false);

  const submitCustomization = useCallback(() => {
    const useSpecificFlagsColoring = selectedKey === COLORING_SCHEME_OPTION.BY_FLAG_COLOR;
    void dispatch(updateUserParams({ useSpecificFlagsColoring, condensedFlagsDisplay: condensedRegionFlags }));
    props.onClose();
  }, [selectedKey, condensedRegionFlags]);

  useEffect(() => {
    setCondensedRegionFlags(condensedFlagsDisplay);
  }, [condensedFlagsDisplay]);

  return (
    <DialogComponent
      mainButton={{
        label: "Save",
        onPress: submitCustomization,
      }}
      title={`Customize Flags Track`}
      closeFn={props.onClose}
      contentStyle={{ minWidth: 500 }}
    >
      <VStack>
        <RadioGroup
          label={"Color flags by"}
          defaultValue={defaultColoringSchemeOptionKey}
          isRequired={true}
          onChange={setSelectedKey}
        >
          {colorSchemeOptions.map((option) => {
            return (
              <Radio key={option.key} value={option.key} description={option.description}>
                {option.label}
              </Radio>
            );
          })}
        </RadioGroup>

        <Switch
          title={"Condensed view"}
          isSelected={condensedRegionFlags}
          onChange={setCondensedRegionFlags}
          isRequired={true}
          isDisabled={false}
        />
      </VStack>
    </DialogComponent>
  );
};

export { FlagTrackCustomizationDialog };
